import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../services/apiService';
import { Company, SubscriptionPlan, User, UserRole, PlanConfig, CurrencyConfig } from '../types';
import AddCompanyModal from '../components/AddCompanyModal';
import EditCompanyModal from '../components/EditCompanyModal';
import AddUserModal from '../components/AddUserModal';
import SecurityTab from '../components/SecurityTab';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, Line, LineChart, Legend } from 'recharts';
import { toast } from '../services/toast';

// --- Composant Audit Log ---
const AuditLogView: React.FC = () => {
  const [logs, setLogs] = useState<Array<{ id: number; user_name: string; user_role: string; action: string; target_type: string; target_id: string; details: string; ip_address: string; created_at: string }>>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await api.getAuditLogs({ page, limit: 30, filterAction: filterAction || undefined, filterUser: filterUser || undefined });
      setLogs(data.logs || []);
      setPages(data.pages || 0);
      setTotal(data.total || 0);
      setLoading(false);
    };
    load();
  }, [page, filterAction, filterUser]);

  const actionLabels: Record<string, string> = {
    create_company: 'Création entreprise', update_company_status: 'Statut entreprise',
    create_user: 'Création utilisateur', delete_user: 'Suppression utilisateur',
    bulk_update_leads: 'MAJ masse prospects', bulk_delete_leads: 'Suppression masse prospects',
    update_profile: 'Modification profil',
  };

  const actionColor = (a: string) => {
    if (a.includes('delete') || a.includes('bulk_delete')) return 'text-rose-400 bg-rose-500/10';
    if (a.includes('create')) return 'text-emerald-400 bg-emerald-500/10';
    if (a.includes('update') || a.includes('status')) return 'text-amber-400 bg-amber-500/10';
    return 'text-slate-400 bg-slate-500/10';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-2xl font-black text-[#e0eaff] mb-1">Journal d'audit</h3>
            <p className="text-[#7f93bc] text-sm">{total} entrée{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            <input type="text" placeholder="Filtrer par action..." value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} className="orion-lead-select text-xs px-3 py-2 rounded-xl w-44" />
            <input type="text" placeholder="Filtrer par utilisateur..." value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }} className="orion-lead-select text-xs px-3 py-2 rounded-xl w-44" />
          </div>
        </div>
        {loading ? (
          <div className="text-center py-16 text-[#546a96] text-sm">Chargement...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-[#546a96] text-sm">Aucune entrée dans le journal</div>
        ) : (
          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="flex items-center gap-4 px-4 py-3 rounded-xl bg-[rgba(14,28,58,0.4)] border border-[rgba(100,140,210,0.08)] hover:border-[rgba(100,140,210,0.2)] transition-colors">
                <div className="w-16 flex-shrink-0">
                  <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${actionColor(log.action)}`}>
                    {log.action.includes('delete') ? 'DEL' : log.action.includes('create') ? 'NEW' : 'UPD'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#e0eaff]">{actionLabels[log.action] || log.action}</span>
                    {log.target_type && <span className="text-[10px] text-[#546a96] font-bold">({log.target_type})</span>}
                  </div>
                  <div className="text-[11px] text-[#546a96] truncate">{log.details || log.target_id || '-'}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-bold text-[#7f93bc]">{log.user_name || 'Système'}</div>
                  <div className="text-[10px] text-[#3d5280]">{log.user_role}</div>
                </div>
                <div className="text-right flex-shrink-0 w-28">
                  <div className="text-[11px] text-[#546a96]">{new Date(log.created_at).toLocaleDateString('fr-FR')}</div>
                  <div className="text-[10px] text-[#3d5280]">{new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div className="text-[10px] text-[#3d5280] w-24 text-right flex-shrink-0">{log.ip_address || '-'}</div>
              </div>
            ))}
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="orion-lead-select px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30">Préc.</button>
            <span className="text-xs font-bold text-[#7f93bc]">{page} / {pages}</span>
            <button type="button" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="orion-lead-select px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30">Suiv.</button>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Composant Charge Serveur ---
type ServerStats = NonNullable<Awaited<ReturnType<typeof api.getServerStats>>>;
type ServerHistory = NonNullable<Awaited<ReturnType<typeof api.getServerStatsHistory>>>;
type HistoryRange = '1h' | '24h' | '7d' | '30d';
type SecurityData = NonNullable<Awaited<ReturnType<typeof api.getSecuritySignals>>>;
type SecurityWindow = '15m' | '1h' | '24h' | '7d';

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return 'N/A';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + units[i];
};

const formatDuration = (seconds: number): string => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
};

const ServerStatsView: React.FC = () => {
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [liveHistory, setLiveHistory] = useState<Array<{ time: string; users: number; companies: number; mysqlThreads: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const historyMaxPoints = 30;
  const pollMs = 10000;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Historique persisté (server_stats_snapshots)
  const [range, setRange] = useState<HistoryRange>('1h');
  const [histData, setHistData] = useState<ServerHistory | null>(null);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      const data = await api.getServerStats();
      if (cancelled) return;
      if (!data) {
        setError("Impossible de récupérer les statistiques serveur.");
        setLoading(false);
        return;
      }
      setError(null);
      setStats(data);
      setLoading(false);
      const label = new Date(data.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLiveHistory(prev => {
        const next = [...prev, {
          time: label,
          users: data.sessions.users_active,
          companies: data.sessions.companies_active,
          mysqlThreads: data.mysql.Threads_connected,
        }];
        return next.length > historyMaxPoints ? next.slice(next.length - historyMaxPoints) : next;
      });
    };
    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, pollMs);
    return () => { cancelled = true; if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Chargement de l'historique persisté à chaque changement de plage
  useEffect(() => {
    let cancelled = false;
    setHistLoading(true);
    api.getServerStatsHistory(range).then(data => {
      if (cancelled) return;
      setHistData(data);
      setHistLoading(false);
    });
    return () => { cancelled = true; };
  }, [range]);

  const chartTooltipStyle = {
    borderRadius: '12px',
    border: '1px solid rgba(100,140,210,0.25)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
    background: 'rgba(14,28,58,0.95)',
    color: '#e0eaff',
    fontSize: '12px',
    fontWeight: 700,
  };

  if (loading) {
    return <div className="text-center py-16 text-[#546a96] text-sm">Chargement des statistiques serveur...</div>;
  }
  if (error || !stats) {
    return <div className="text-center py-16 text-rose-400 text-sm">{error || 'Erreur'}</div>;
  }

  const diskUsedPct = stats.disk.total_bytes && stats.disk.free_bytes !== null
    ? Math.round(((stats.disk.total_bytes - (stats.disk.free_bytes || 0)) / stats.disk.total_bytes) * 100)
    : null;

  const diskUsedBytes = stats.disk.total_bytes && stats.disk.free_bytes !== null
    ? stats.disk.total_bytes - (stats.disk.free_bytes || 0)
    : null;

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <div className="orion-admin-card p-6 rounded-2xl">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Utilisateurs en ligne</div>
          <div className="text-4xl font-black text-cyan-300">{stats.sessions.users_active}</div>
          <div className="text-[10px] text-[#546a96] mt-2">Fenêtre : {stats.sessions.window_minutes} min · Total : {stats.totals.users}</div>
        </div>
        <div className="orion-admin-card p-6 rounded-2xl">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Compagnies actives</div>
          <div className="text-4xl font-black text-emerald-400">{stats.sessions.companies_active}</div>
          <div className="text-[10px] text-[#546a96] mt-2">sur {stats.totals.companies} déployée(s)</div>
        </div>
        <div className="orion-admin-card p-6 rounded-2xl">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Connexions MySQL</div>
          <div className="text-4xl font-black text-indigo-300">{stats.mysql.Threads_connected}<span className="text-base text-[#546a96]"> / {stats.innodb.max_connections || '?'}</span></div>
          <div className="text-[10px] text-[#546a96] mt-2">Actives : {stats.mysql.Threads_running} · Pic : {stats.mysql.Max_used_connections} · Uptime {formatDuration(stats.mysql.Uptime)}</div>
        </div>
        <div className="orion-admin-card p-6 rounded-2xl">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">MySQL Buffer Pool</div>
          {stats.innodb.hit_rate_pct !== null ? (
            <div className={`text-4xl font-black ${stats.innodb.hit_rate_pct >= 99 ? 'text-emerald-400' : stats.innodb.hit_rate_pct >= 95 ? 'text-amber-300' : 'text-rose-400'}`}>
              {stats.innodb.hit_rate_pct.toFixed(2)}<span className="text-xl">%</span>
            </div>
          ) : (
            <div className="text-base font-bold text-[#546a96]">Pas d'activité</div>
          )}
          <div className="text-[10px] text-[#546a96] mt-2">Hit rate ≥ 99% = RAM suffisante</div>
        </div>
        <div className="orion-admin-card p-6 rounded-2xl">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Charge CPU (1/5/15 min)</div>
          {stats.cpu_load ? (
            <div className="text-2xl font-black text-amber-300">
              {stats.cpu_load['1min']} / {stats.cpu_load['5min']} / {stats.cpu_load['15min']}
            </div>
          ) : (
            <div className="text-base font-bold text-[#546a96]">Non dispo. ({stats.os})</div>
          )}
          <div className="text-[10px] text-[#546a96] mt-2">PHP {stats.php_version} · {stats.os}</div>
        </div>
      </div>

      {/* Historique persisté (snapshots serveur) */}
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-black text-[#e0eaff]">Historique de charge</h3>
            <p className="text-xs text-[#7f93bc] mt-1">
              Snapshots serveur persistés · {histData ? `${histData.buckets.length} bucket(s)` : '—'}
            </p>
          </div>
          <div className="flex gap-1 bg-[rgba(15,30,60,0.6)] p-1 rounded-xl border border-[rgba(100,140,210,0.15)]">
            {(['1h','24h','7d','30d'] as HistoryRange[]).map(r => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide transition-all ${range === r ? 'bg-cyan-500/25 text-cyan-300' : 'text-[#7f93bc] hover:text-[#e0eaff]'}`}
              >
                {r === '1h' ? '1 h' : r === '24h' ? '24 h' : r === '7d' ? '7 j' : '30 j'}
              </button>
            ))}
          </div>
        </div>

        {histLoading && !histData ? (
          <div className="text-center py-16 text-[#546a96] text-sm">Chargement de l'historique...</div>
        ) : !histData || histData.buckets.length === 0 ? (
          <div className="text-center py-12 text-[#546a96] text-sm">
            Aucun snapshot sur cette plage — les données s'accumulent au fur et à mesure de l'activité API.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
                <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">Requêtes API</div>
                <div className="text-xl font-black text-[#e0eaff]">{histData.perf_global.count.toLocaleString('fr-FR')}</div>
              </div>
              <div className="p-4 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
                <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">Latence p50 / p95</div>
                <div className="text-xl font-black text-cyan-300">{histData.perf_global.p50_ms ?? '—'}<span className="text-xs text-[#546a96]">ms</span> / {histData.perf_global.p95_ms ?? '—'}<span className="text-xs text-[#546a96]">ms</span></div>
              </div>
              <div className="p-4 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
                <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">Latence p99</div>
                <div className="text-xl font-black text-indigo-300">{histData.perf_global.p99_ms ?? '—'}<span className="text-xs text-[#546a96]">ms</span></div>
              </div>
              <div className="p-4 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
                <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">Taux d'erreur 5xx</div>
                <div className={`text-xl font-black ${histData.perf_global.error_rate_5xx_pct > 1 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {histData.perf_global.error_rate_5xx_pct}<span className="text-xs text-[#546a96]">%</span>
                </div>
                <div className="text-[9px] text-[#546a96] mt-1">{histData.perf_global.errors_5xx} / {histData.perf_global.errors_4xx} 4xx</div>
              </div>
            </div>

            <div className="h-[280px] mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={histData.buckets}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.35}/>
                      <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,140,210,0.15)" />
                  <XAxis dataKey="bucket" axisLine={false} tickLine={false}
                         tickFormatter={(v) => new Date(v).toLocaleTimeString('fr-FR', range === '1h' ? { hour: '2-digit', minute: '2-digit' } : range === '24h' ? { hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: '2-digit' })}
                         tick={{ fill: '#7f93bc', fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={chartTooltipStyle} labelFormatter={(v) => new Date(v as string).toLocaleString('fr-FR')} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#7f93bc' }} />
                  <Area type="monotone" dataKey="users_avg" name="Utilisateurs (moy)" stroke="#22d3ee" strokeWidth={2} fill="url(#colorUsers)" />
                  <Line type="monotone" dataKey="users_max" name="Utilisateurs (pic)" stroke="#6366f1" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={histData.buckets}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,140,210,0.15)" />
                  <XAxis dataKey="bucket" axisLine={false} tickLine={false}
                         tickFormatter={(v) => new Date(v).toLocaleTimeString('fr-FR', range === '1h' ? { hour: '2-digit', minute: '2-digit' } : range === '24h' ? { hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: '2-digit' })}
                         tick={{ fill: '#7f93bc', fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 11 }} unit="ms" />
                  <Tooltip contentStyle={chartTooltipStyle} labelFormatter={(v) => new Date(v as string).toLocaleString('fr-FR')} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#7f93bc' }} />
                  <Line type="monotone" dataKey="api_p50_avg" name="Latence p50" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="api_p95_avg" name="Latence p95" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="api_p99_avg" name="Latence p99" stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* Top endpoints lents */}
      {histData && histData.top_slow.length > 0 && (
        <div className="orion-admin-card p-8 rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-black text-[#e0eaff]">Top endpoints les plus lents</h3>
              <p className="text-xs text-[#7f93bc] mt-1">Classés par p95 · rétention détail 7 j</p>
            </div>
          </div>
          <div className="space-y-2">
            {histData.top_slow.map((row, i) => (
              <div key={row.action} className="flex items-center gap-4 px-4 py-3 rounded-xl bg-[rgba(14,28,58,0.4)] border border-[rgba(100,140,210,0.08)]">
                <div className="w-8 text-center">
                  <span className="text-[10px] font-black text-[#546a96]">#{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#e0eaff] font-mono truncate">{row.action}</div>
                  <div className="text-[10px] text-[#546a96]">{row.count.toLocaleString('fr-FR')} appel(s)</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-black uppercase text-[#7f93bc]">p50</div>
                  <div className="text-sm font-black text-emerald-400">{row.p50_ms ?? '—'}<span className="text-[10px] text-[#546a96]">ms</span></div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-black uppercase text-[#7f93bc]">p95</div>
                  <div className={`text-sm font-black ${(row.p95_ms ?? 0) > 1000 ? 'text-rose-400' : (row.p95_ms ?? 0) > 500 ? 'text-amber-300' : 'text-cyan-300'}`}>
                    {row.p95_ms ?? '—'}<span className="text-[10px] text-[#546a96]">ms</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-black uppercase text-[#7f93bc]">max</div>
                  <div className="text-sm font-black text-indigo-300">{row.max_ms}<span className="text-[10px] text-[#546a96]">ms</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historique polling */}
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-black text-[#e0eaff]">Charge en temps réel</h3>
            <p className="text-xs text-[#7f93bc] mt-1">Polling toutes les {pollMs / 1000}s · {liveHistory.length} point(s) conservés</p>
          </div>
          <span className="text-xs font-bold text-emerald-400 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            En direct
          </span>
        </div>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={liveHistory}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,140,210,0.15)" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 10 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#7f93bc' }} />
              <Line type="monotone" dataKey="users" name="Utilisateurs" stroke="#22d3ee" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="companies" name="Compagnies" stroke="#10b981" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="mysqlThreads" name="MySQL threads" stroke="#6366f1" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Disque + BDD */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="orion-admin-card p-8 rounded-2xl">
          <h3 className="text-lg font-black text-[#e0eaff] mb-6">Stockage disque</h3>
          <div className="text-xs font-bold text-[#7f93bc] mb-2">Point de montage : <span className="text-[#e0eaff] font-mono">{stats.disk.path}</span></div>
          {stats.disk.total_bytes !== null && diskUsedPct !== null ? (
            <>
              <div className="flex items-end justify-between mb-2">
                <span className="text-3xl font-black text-[#e0eaff]">{formatBytes(diskUsedBytes)}</span>
                <span className="text-xs font-bold text-[#7f93bc]">/ {formatBytes(stats.disk.total_bytes)}</span>
              </div>
              <div className="h-3 rounded-full bg-[rgba(100,140,210,0.1)] overflow-hidden">
                <div
                  className={`h-full transition-all ${diskUsedPct > 90 ? 'bg-rose-500' : diskUsedPct > 75 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                  style={{ width: `${diskUsedPct}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-bold text-[#546a96]">
                <span>Utilisé {diskUsedPct}%</span>
                <span>Libre {formatBytes(stats.disk.free_bytes)}</span>
              </div>
            </>
          ) : (
            <div className="text-sm text-[#546a96]">Informations disque indisponibles</div>
          )}
        </div>

        <div className="orion-admin-card p-8 rounded-2xl">
          <h3 className="text-lg font-black text-[#e0eaff] mb-6">Base de données</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
              <span className="text-[10px] font-bold text-[#546a96] uppercase">Schéma</span>
              <span className="font-black text-[#e0eaff] font-mono">{stats.database.name}</span>
            </div>
            <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
              <span className="text-[10px] font-bold text-[#546a96] uppercase">Taille totale</span>
              <span className="font-black text-cyan-300">{formatBytes(stats.database.bytes)}</span>
            </div>
            <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
              <span className="text-[10px] font-bold text-[#546a96] uppercase">Requêtes traitées</span>
              <span className="font-black text-[#e0eaff]">{stats.mysql.Questions.toLocaleString('fr-FR')}</span>
            </div>
            <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
              <span className="text-[10px] font-bold text-[#546a96] uppercase">Requêtes lentes</span>
              <span className={`font-black ${stats.mysql.Slow_queries > 100 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {stats.mysql.Slow_queries.toLocaleString('fr-FR')}
              </span>
            </div>
            <div className="flex justify-between items-end">
              <span className="text-[10px] font-bold text-[#546a96] uppercase">Mémoire PHP (pic)</span>
              <span className="font-black text-[#e0eaff]">{formatBytes(stats.php_memory.peak_bytes)} / {stats.php_memory.limit}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top compagnies actives */}
      {stats.sessions.by_company.length > 0 && (
        <div className="orion-admin-card p-8 rounded-2xl">
          <h3 className="text-lg font-black text-[#e0eaff] mb-6">Top compagnies actives</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.sessions.by_company} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(100,140,210,0.15)" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="company_name" axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 11 }} width={140} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="users" name="Utilisateurs" fill="#22d3ee" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Composant Sécurité ---
const threatBadge = (level: 'high' | 'medium' | 'low') => {
  if (level === 'high') return { label: 'ALERTE ÉLEVÉE', cls: 'text-rose-300 bg-rose-500/20 border-rose-500/40' };
  if (level === 'medium') return { label: 'VIGILANCE', cls: 'text-amber-300 bg-amber-500/20 border-amber-500/40' };
  return { label: 'NOMINAL', cls: 'text-emerald-300 bg-emerald-500/20 border-emerald-500/40' };
};

const fmtDateTime = (s: string) => {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const SecurityView: React.FC = () => {
  const [data, setData] = useState<SecurityData | null>(null);
  const [window, setWindow] = useState<SecurityWindow>('1h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      const res = await api.getSecuritySignals(window);
      if (cancelled) return;
      if (!res) {
        setError("Impossible de récupérer les signaux de sécurité.");
        setLoading(false);
        return;
      }
      setError(null);
      setData(res);
      setLoading(false);
    };
    setLoading(true);
    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, 30000);
    return () => { cancelled = true; if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [window]);

  if (loading && !data) {
    return <div className="orion-admin-card p-10 rounded-2xl text-center text-[#7f93bc]">Analyse en cours...</div>;
  }
  if (error) {
    return <div className="orion-admin-card p-10 rounded-2xl text-center text-rose-400">{error}</div>;
  }
  if (!data) return null;

  const badge = threatBadge(data.threat_level);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      {/* En-tête niveau de menace */}
      <div className="orion-admin-card p-8 rounded-2xl flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-[#7f93bc] mb-2">Niveau de menace</div>
          <div className={`inline-flex items-center px-4 py-2 rounded-xl border text-base font-black tracking-wider ${badge.cls}`}>
            {badge.label}
          </div>
          <p className="text-xs text-[#7f93bc] mt-3">
            Fenêtre d'analyse : {data.window_minutes} min · Rafraîchissement 30 s
          </p>
        </div>
        <div className="flex gap-1 bg-[rgba(15,30,60,0.6)] p-1 rounded-xl border border-[rgba(100,140,210,0.15)]">
          {(['15m','1h','24h','7d'] as SecurityWindow[]).map(w => (
            <button
              key={w}
              type="button"
              onClick={() => setWindow(w)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wide transition-all ${window === w ? 'bg-cyan-500/25 text-cyan-300' : 'text-[#7f93bc] hover:text-[#e0eaff]'}`}
            >
              {w === '15m' ? '15 min' : w === '1h' ? '1 h' : w === '24h' ? '24 h' : '7 j'}
            </button>
          ))}
        </div>
      </div>

      {/* Résumé compteurs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Brute-force', value: data.summary.brute_force_ips, color: 'text-rose-400', hint: `≥ ${data.thresholds.brute_force_min_failed} échecs` },
          { label: 'Scanning', value: data.summary.scanning_ips, color: 'text-amber-300', hint: `≥ ${data.thresholds.scanning_min_4xx} × 4xx` },
          { label: 'Flood', value: data.summary.flood_ips, color: 'text-rose-400', hint: `≥ ${data.thresholds.flood_min_requests_per_h}/h` },
          { label: 'Hijacking', value: data.summary.hijacking_suspects, color: 'text-rose-400', hint: '2+ IPs / user' },
          { label: 'Bots / scripts', value: data.summary.suspicious_agents, color: 'text-amber-300', hint: 'UA suspects' },
          { label: 'Exfiltration', value: data.summary.exfiltration_suspects, color: 'text-amber-300', hint: `≥ ${data.thresholds.exfiltration_min_actions} actions` },
        ].map(k => (
          <div key={k.label} className="orion-admin-card p-5 rounded-2xl">
            <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">{k.label}</div>
            <div className={`text-3xl font-black ${k.value > 0 ? k.color : 'text-[#546a96]'}`}>{k.value}</div>
            <div className="text-[10px] text-[#546a96] mt-2">{k.hint}</div>
          </div>
        ))}
      </div>

      {/* Brute-force */}
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-[#e0eaff]">Brute-force login</h3>
            <p className="text-xs text-[#7f93bc] mt-1">IPs avec ≥ {data.thresholds.brute_force_min_failed} échecs de connexion</p>
          </div>
          <span className="text-xs font-black text-rose-400">{data.brute_force.length}</span>
        </div>
        {data.brute_force.length === 0 ? (
          <div className="text-center py-6 text-[#546a96] text-sm">Aucune IP suspecte détectée.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#7f93bc] border-b border-[rgba(100,140,210,0.15)]">
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">IP</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Tentatives</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Échecs</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Réussis</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Dernière</th>
                </tr>
              </thead>
              <tbody>
                {data.brute_force.map((row, i) => (
                  <tr key={i} className="border-b border-[rgba(100,140,210,0.08)] hover:bg-[rgba(100,140,210,0.05)]">
                    <td className="py-2 px-2 font-mono text-[#e0eaff]">{row.ip_address}</td>
                    <td className="py-2 px-2 text-right text-[#e0eaff]">{row.attempts}</td>
                    <td className="py-2 px-2 text-right text-rose-400 font-black">{row.failed}</td>
                    <td className={`py-2 px-2 text-right font-black ${Number(row.success) > 0 ? 'text-amber-300' : 'text-[#546a96]'}`}>{row.success}</td>
                    <td className="py-2 px-2 text-right text-[#7f93bc]">{fmtDateTime(row.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scanning & Flood */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="orion-admin-card p-8 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-black text-[#e0eaff]">Scanning</h3>
              <p className="text-xs text-[#7f93bc] mt-1">IPs générant beaucoup de 4xx</p>
            </div>
            <span className="text-xs font-black text-amber-300">{data.scanning.length}</span>
          </div>
          {data.scanning.length === 0 ? (
            <div className="text-center py-6 text-[#546a96] text-sm">Aucun scan détecté.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#7f93bc] border-b border-[rgba(100,140,210,0.15)]">
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">IP</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">4xx</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Endpoints</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Dernière</th>
                </tr>
              </thead>
              <tbody>
                {data.scanning.map((row, i) => (
                  <tr key={i} className="border-b border-[rgba(100,140,210,0.08)]">
                    <td className="py-2 px-2 font-mono text-[#e0eaff]">{row.ip_address}</td>
                    <td className="py-2 px-2 text-right text-amber-300 font-black">{row.c4xx}</td>
                    <td className="py-2 px-2 text-right text-[#e0eaff]">{row.distinct_actions}</td>
                    <td className="py-2 px-2 text-right text-[#7f93bc]">{fmtDateTime(row.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="orion-admin-card p-8 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-black text-[#e0eaff]">Flood / volume</h3>
              <p className="text-xs text-[#7f93bc] mt-1">Top 15 IPs · seuil ≥ {data.thresholds.flood_min_requests_per_h}/h</p>
            </div>
            <span className="text-xs font-black text-rose-400">{data.flood.length}</span>
          </div>
          {data.top_ips.length === 0 ? (
            <div className="text-center py-6 text-[#546a96] text-sm">Aucune activité IP enregistrée.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#7f93bc] border-b border-[rgba(100,140,210,0.15)]">
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">IP</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Total</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">4xx</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">5xx</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Moy ms</th>
                </tr>
              </thead>
              <tbody>
                {data.top_ips.map((row, i) => {
                  const isFlood = data.flood.some(f => f.ip_address === row.ip_address);
                  return (
                    <tr key={i} className={`border-b border-[rgba(100,140,210,0.08)] ${isFlood ? 'bg-rose-500/5' : ''}`}>
                      <td className="py-2 px-2 font-mono text-[#e0eaff]">
                        {row.ip_address}
                        {isFlood && <span className="ml-2 text-[9px] text-rose-400 font-black">FLOOD</span>}
                      </td>
                      <td className={`py-2 px-2 text-right font-black ${isFlood ? 'text-rose-400' : 'text-[#e0eaff]'}`}>{row.total}</td>
                      <td className="py-2 px-2 text-right text-amber-300">{row.c4xx}</td>
                      <td className="py-2 px-2 text-right text-rose-400">{row.c5xx}</td>
                      <td className="py-2 px-2 text-right text-[#7f93bc]">{row.avg_ms}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Hijacking */}
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-[#e0eaff]">Détournement de session</h3>
            <p className="text-xs text-[#7f93bc] mt-1">Utilisateurs actifs depuis 2+ IPs simultanément (fenêtre 10 min)</p>
          </div>
          <span className="text-xs font-black text-rose-400">{data.hijacking.length}</span>
        </div>
        {data.hijacking.length === 0 ? (
          <div className="text-center py-6 text-[#546a96] text-sm">Aucun détournement suspecté.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#7f93bc] border-b border-[rgba(100,140,210,0.15)]">
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">Utilisateur</th>
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">Compagnie</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">IPs</th>
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">Adresses</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Dernière</th>
                </tr>
              </thead>
              <tbody>
                {data.hijacking.map((row, i) => (
                  <tr key={i} className="border-b border-[rgba(100,140,210,0.08)]">
                    <td className="py-2 px-2 text-[#e0eaff]">
                      <div className="font-black">{row.user_name}</div>
                      <div className="text-[10px] text-[#7f93bc]">{row.user_email}</div>
                    </td>
                    <td className="py-2 px-2 text-[#7f93bc]">{row.company_name}</td>
                    <td className="py-2 px-2 text-right text-rose-400 font-black">{row.ip_count}</td>
                    <td className="py-2 px-2 font-mono text-[10px] text-[#7f93bc] max-w-xs truncate" title={row.ips}>{row.ips}</td>
                    <td className="py-2 px-2 text-right text-[#7f93bc]">{fmtDateTime(row.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User-agents suspects & exfiltration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="orion-admin-card p-8 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-black text-[#e0eaff]">Agents suspects</h3>
              <p className="text-xs text-[#7f93bc] mt-1">User-agents = bots, scripts, scanners</p>
            </div>
            <span className="text-xs font-black text-amber-300">{data.suspicious_agents.length}</span>
          </div>
          {data.suspicious_agents.length === 0 ? (
            <div className="text-center py-6 text-[#546a96] text-sm">Aucun agent suspect.</div>
          ) : (
            <div className="space-y-2">
              {data.suspicious_agents.map((row, i) => (
                <div key={i} className="p-3 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-black text-[#e0eaff]">{row.user_name}</span>
                    <span className="text-[10px] text-[#7f93bc]">{fmtDateTime(row.last_seen)}</span>
                  </div>
                  <div className="text-[10px] font-mono text-amber-300 truncate" title={row.user_agent}>{row.user_agent}</div>
                  <div className="text-[10px] font-mono text-[#7f93bc] mt-0.5">{row.ip_address}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="orion-admin-card p-8 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-black text-[#e0eaff]">Exfiltration probable</h3>
              <p className="text-xs text-[#7f93bc] mt-1">Actions massives : bulk / export RGPD / suppression RGPD</p>
            </div>
            <span className="text-xs font-black text-amber-300">{data.exfiltration.length}</span>
          </div>
          {data.exfiltration.length === 0 ? (
            <div className="text-center py-6 text-[#546a96] text-sm">Aucune activité d'exfiltration.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#7f93bc] border-b border-[rgba(100,140,210,0.15)]">
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">Utilisateur</th>
                  <th className="text-left py-2 px-2 font-black uppercase tracking-wide">Action</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Nb</th>
                  <th className="text-right py-2 px-2 font-black uppercase tracking-wide">Dernière</th>
                </tr>
              </thead>
              <tbody>
                {data.exfiltration.map((row, i) => (
                  <tr key={i} className="border-b border-[rgba(100,140,210,0.08)]">
                    <td className="py-2 px-2 text-[#e0eaff]">{row.user_name}</td>
                    <td className="py-2 px-2 font-mono text-[10px] text-amber-300">{row.action}</td>
                    <td className="py-2 px-2 text-right font-black text-amber-300">{row.cnt}</td>
                    <td className="py-2 px-2 text-right text-[#7f93bc]">{fmtDateTime(row.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Widget compact : Charge serveur (dashboard) ---
const ServerStatsWidget: React.FC<{ onNavigate?: (v: any) => void }> = ({ onNavigate }) => {
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      const data = await api.getServerStats();
      if (cancelled) return;
      setStats(data);
      setLoading(false);
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div className="orion-admin-card p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-black text-[#e0eaff] tracking-tight">Charge serveur</h3>
          <p className="text-[10px] text-[#7f93bc] mt-0.5">Mise à jour auto · 30 s</p>
        </div>
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('server')}
            className="text-[10px] font-black uppercase tracking-wide text-cyan-300 hover:text-cyan-200 px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/20 transition-colors"
          >
            Détails →
          </button>
        )}
      </div>
      {loading ? (
        <div className="py-8 text-center text-xs text-[#546a96]">Chargement...</div>
      ) : !stats ? (
        <div className="py-8 text-center text-xs text-rose-400">Indisponible</div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
            <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">Utilisateurs en ligne</div>
            <div className="text-2xl font-black text-cyan-300">{stats.sessions.users_active}</div>
            <div className="text-[9px] text-[#546a96] mt-0.5">{stats.sessions.companies_active} compagnies actives</div>
          </div>
          <div className="p-3 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
            <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">MySQL</div>
            <div className="text-2xl font-black text-indigo-300">
              {stats.mysql.Threads_connected}
              <span className="text-xs text-[#546a96]"> / {stats.innodb.max_connections || '?'}</span>
            </div>
            <div className="text-[9px] text-[#546a96] mt-0.5">{stats.mysql.Threads_running} actives</div>
          </div>
          <div className="p-3 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
            <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">Buffer Pool</div>
            {stats.innodb.hit_rate_pct !== null ? (
              <div className={`text-2xl font-black ${stats.innodb.hit_rate_pct >= 99 ? 'text-emerald-400' : stats.innodb.hit_rate_pct >= 95 ? 'text-amber-300' : 'text-rose-400'}`}>
                {stats.innodb.hit_rate_pct.toFixed(1)}<span className="text-sm">%</span>
              </div>
            ) : (
              <div className="text-sm font-bold text-[#546a96]">—</div>
            )}
            <div className="text-[9px] text-[#546a96] mt-0.5">hit rate InnoDB</div>
          </div>
          <div className="p-3 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)]">
            <div className="text-[9px] font-black uppercase text-[#7f93bc] mb-1">CPU (1 min)</div>
            {stats.cpu_load ? (
              <div className="text-2xl font-black text-amber-300">{stats.cpu_load['1min']}</div>
            ) : (
              <div className="text-sm font-bold text-[#546a96]">{stats.os}</div>
            )}
            <div className="text-[9px] text-[#546a96] mt-0.5">PHP {stats.php_version}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Widget compact : Sécurité (dashboard) ---
const SecurityWidget: React.FC<{ onNavigate?: (v: any) => void }> = ({ onNavigate }) => {
  const [data, setData] = useState<SecurityData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      const res = await api.getSecuritySignals('1h');
      if (cancelled) return;
      setData(res);
      setLoading(false);
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const badge = data ? threatBadge(data.threat_level) : null;

  return (
    <div className="orion-admin-card p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-black text-[#e0eaff] tracking-tight">Sécurité</h3>
          <p className="text-[10px] text-[#7f93bc] mt-0.5">Fenêtre 1 h · 30 s</p>
        </div>
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('security')}
            className="text-[10px] font-black uppercase tracking-wide text-cyan-300 hover:text-cyan-200 px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/20 transition-colors"
          >
            Détails →
          </button>
        )}
      </div>
      {loading ? (
        <div className="py-8 text-center text-xs text-[#546a96]">Analyse...</div>
      ) : !data ? (
        <div className="py-8 text-center text-xs text-rose-400">Indisponible</div>
      ) : (
        <>
          <div className={`mb-4 inline-flex items-center px-3 py-1.5 rounded-lg border text-xs font-black tracking-wider ${badge!.cls}`}>
            {badge!.label}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Brute-force', value: data.summary.brute_force_ips, color: 'text-rose-400' },
              { label: 'Scanning', value: data.summary.scanning_ips, color: 'text-amber-300' },
              { label: 'Flood', value: data.summary.flood_ips, color: 'text-rose-400' },
              { label: 'Hijacking', value: data.summary.hijacking_suspects, color: 'text-rose-400' },
              { label: 'Agents susp.', value: data.summary.suspicious_agents, color: 'text-amber-300' },
              { label: 'Exfiltration', value: data.summary.exfiltration_suspects, color: 'text-amber-300' },
            ].map(k => (
              <div key={k.label} className="p-2.5 rounded-xl bg-[rgba(15,30,60,0.5)] border border-[rgba(100,140,210,0.15)] flex items-center justify-between">
                <div className="text-[10px] font-black uppercase text-[#7f93bc]">{k.label}</div>
                <div className={`text-lg font-black ${k.value > 0 ? k.color : 'text-[#546a96]'}`}>{k.value}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

interface SuperAdminDashboardProps {
  activeView: string;
  onImpersonate: (user: User) => void;
  currentUser: User;
  onNavigate?: (view: any) => void;
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ activeView, onImpersonate, currentUser, onNavigate }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [systemUsers, setSystemUsers] = useState<User[]>([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [showAddSystemUser, setShowAddSystemUser] = useState(false);
  const [loading, setLoading] = useState(true);

  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyConfig[]>([]);

  const [editingPlan, setEditingPlan] = useState<PlanConfig | null>(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);

  const [newCurrency, setNewCurrency] = useState<CurrencyConfig>({ code: '', name: '', symbol: '' });

  // État édition membre équipe
  const [editingSystemUser, setEditingSystemUser] = useState<(User & { password?: string }) | null>(null);

  // SMTP global
  const [globalSmtp, setGlobalSmtp] = useState({ smtp_host: '', smtp_port: 587, smtp_enc: 'tls', smtp_user: '', smtp_pass: '', from_name: '', from_email: '' });
  const [globalSmtpHasPassword, setGlobalSmtpHasPassword] = useState(false);
  const [globalSmtpMsg, setGlobalSmtpMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [globalSmtpSaving, setGlobalSmtpSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const [c, u, config] = await Promise.all([
        api.getCompanies(),
        api.getSuperAdmins(),
        api.getSystemConfig()
    ]);
    setCompanies(c || []);
    setSystemUsers(u || []);
    setPlans(config.plans);
    setCurrencies(config.currencies);

    // Charger SMTP global
    const smtp = await api.getGlobalSmtp();
    if (smtp) {
      setGlobalSmtp(prev => ({ ...prev, smtp_host: smtp.smtp_host, smtp_port: smtp.smtp_port, smtp_enc: smtp.smtp_enc, smtp_user: smtp.smtp_user, from_name: smtp.from_name || '', from_email: smtp.from_email || '' }));
      setGlobalSmtpHasPassword(smtp.has_password);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleStatus = async (company: Company) => {
    if (currentUser.role !== UserRole.SUPER_ADMIN) {
        toast.error("Action refusée. Seul un Super Administrateur peut suspendre un client.");
        return;
    }
    const newStatus = company.status === 'active' ? 'suspended' : 'active';
    setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, status: newStatus } : c));
    await api.updateCompanyStatus(company.id, newStatus);
  };

  const handleDeleteCompany = async (id: string) => {
      if (currentUser.role !== UserRole.SUPER_ADMIN) {
        toast.error("Action refusée. Seul un Super Administrateur peut supprimer un client.");
        return;
    }
    const company = companies.find(c => c.id === id);
    const name = company?.name || '';
    const input = window.prompt(`Cette action est IRRÉVERSIBLE.\nToutes les données (utilisateurs, prospects, opportunités, tâches) seront supprimées.\n\nPour confirmer, tapez le nom de l'entreprise :\n« ${name} »`);
    if (input === null) return;
    if (input.trim() !== name.trim()) {
        toast.warning('Le nom ne correspond pas. Suppression annulée.');
        return;
    }
    const previous = companies;
    setCompanies(prev => prev.filter(c => c.id !== id));
    try {
        await api.deleteCompany(id);
    } catch (err: any) {
        setCompanies(previous);
        toast.error(err?.message || "Erreur lors de la suppression de l'entreprise.");
    }
  };

  const handleCreateCompany = async (data: any) => {
    const newId = 'c_' + Date.now() + Math.random().toString(36).substr(2, 5);
    const optimisticCompany: Company = {
        id: newId,
        name: data.name,
        plan: data.plan || 'BASIC',
        userLimit: data.userLimit || 3,
        activeLeads: 0,
        status: 'active',
        createdAt: new Date().toISOString(),
        currency: 'EUR'
    };

    setCompanies(prev => [optimisticCompany, ...prev]);

    try {
        await api.createCompany({ ...data, id: newId });
        // Rafraîchir depuis le serveur pour avoir les données réelles
        const updatedCompanies = await api.getCompanies();
        if (updatedCompanies && updatedCompanies.length > 0) {
            setCompanies(updatedCompanies);
        }
    } catch (err: any) {
        // Annuler l'update optimiste en cas d'erreur
        setCompanies(prev => prev.filter(c => c.id !== newId));
        throw err; // Re-propagé pour que AddCompanyModal affiche l'erreur
    }
  };

  const handleUpdateCompany = (updates: Partial<Company>) => {
      if (!editingCompany) return;
      const targetId = editingCompany.id;

      const updatedCompany = { ...editingCompany, ...updates };
      setCompanies(prev => prev.map(c => c.id === targetId ? updatedCompany : c));

      api.updateCompany(targetId, updates).catch(err => {
          console.error("Background update failed", err);
      });
  };

  const handleCreateSystemUser = async (data: any) => {
     try {
       const newUser = await api.createUser({
           ...data,
           companyId: 'system'
       });
       setSystemUsers(prev => [...prev, newUser]);
       toast.success(`Utilisateur ${newUser.name} créé.`);
     } catch (err: any) {
       toast.error(err?.message || "Erreur lors de la création de l'utilisateur.");
       throw err; // remonte au modal pour qu'il garde l'état "création en cours"
     }
  };

  const handleUpdateSystemUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSystemUser) return;
    try {
      const updates: any = {
        name: editingSystemUser.name,
        email: editingSystemUser.email,
        role: editingSystemUser.role,
      };
      if (editingSystemUser.password) {
        updates.password = editingSystemUser.password;
      }
      await api.updateUser(editingSystemUser.id, updates);
      setSystemUsers(prev => prev.map(u => u.id === editingSystemUser.id ? { ...u, name: editingSystemUser.name, email: editingSystemUser.email, role: editingSystemUser.role } : u));
      setEditingSystemUser(null);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const handleDeleteSystemUser = async (userId: string) => {
    if (userId === currentUser.id) {
      toast.warning("Vous ne pouvez pas supprimer votre propre compte.");
      return;
    }
    if (!confirm("Supprimer définitivement ce membre ?")) return;
    try {
      await api.deleteUser(userId);
      setSystemUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const impersonateCompany = (targetCompany: Company) => {
    const virtualAdmin: User = {
        id: 'impersonate_' + targetCompany.id + '_' + Date.now(),
        companyId: targetCompany.id,
        name: `Admin ${targetCompany.name}`,
        email: `admin@${targetCompany.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
        role: UserRole.COMPANY_ADMIN,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${targetCompany.id}`
    };
    onImpersonate(virtualAdmin);
  };

  // --- GESTION PLANS ---
  const handleSavePlan = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!editingPlan) return;

      let newPlans = [...plans];
      const existingIndex = plans.findIndex(p => p.id === editingPlan.id);

      if (existingIndex >= 0) {
          newPlans[existingIndex] = editingPlan;
      } else {
          newPlans = [...plans, editingPlan];
      }

      setPlans(newPlans);
      await api.updateSystemConfig({ plans: newPlans });

      setIsEditingPlan(false);
      setEditingPlan(null);
  };

  const startEditPlan = (plan: PlanConfig) => {
      setEditingPlan({...plan});
      setIsEditingPlan(true);
  };

  const startNewPlan = () => {
      setEditingPlan({
          id: '',
          label: '',
          priceMonthly: 0,
          priceYearly: 0,
          userLimit: 1
      });
      setIsEditingPlan(true);
  };

  const deletePlan = async (id: string) => {
      if(confirm('Supprimer ce plan ?')) {
          const newPlans = plans.filter(p => p.id !== id);
          setPlans(newPlans);
          await api.updateSystemConfig({ plans: newPlans });
      }
  };

  // --- GESTION DEVISES ---
  const addCurrency = async () => {
      if (!newCurrency.code || !newCurrency.name || !newCurrency.symbol) return;
      if (currencies.find(c => c.code === newCurrency.code)) {
          toast.warning("Ce code devise existe déjà.");
          return;
      }
      const newCurrencies = [...currencies, newCurrency];
      setCurrencies(newCurrencies);
      await api.updateSystemConfig({ currencies: newCurrencies });
      setNewCurrency({ code: '', name: '', symbol: '' });
  };

  const removeCurrency = async (code: string) => {
      const newCurrencies = currencies.filter(c => c.code !== code);
      setCurrencies(newCurrencies);
      await api.updateSystemConfig({ currencies: newCurrencies });
  };


  // --- GRAPHIQUES ---
  const planData = useMemo(() => {
    const counts: {[key: string]: number} = {};
    plans.forEach(p => counts[p.id] = 0);
    companies.forEach(c => {
        const planId = c.plan || 'BASIC';
        counts[planId] = (counts[planId] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [companies, plans]);

  const COLORS = ['#94a3b8', '#6366f1', '#10b981', '#f59e0b', '#ef4444'];

  const totalMRR = companies.reduce((acc, c) => {
      const plan = plans.find(p => p.id === c.plan);
      return acc + (plan ? plan.priceMonthly : 0);
  }, 0);

  const revenueData = [
    { name: 'Jan', revenue: totalMRR * 0.8 }, { name: 'Fév', revenue: totalMRR * 0.85 },
    { name: 'Mar', revenue: totalMRR * 0.9 }, { name: 'Avr', revenue: totalMRR * 0.95 },
    { name: 'Mai', revenue: totalMRR }, { name: 'Juin', revenue: totalMRR * 1.1 },
  ];

  const chartTooltipStyle = {
    borderRadius: '12px',
    border: '1px solid rgba(100,140,210,0.25)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
    background: 'rgba(14,28,58,0.95)',
    color: '#e0eaff',
    fontSize: '12px',
    fontWeight: 700,
  };

  // --- VUE DASHBOARD ---
  if (activeView === 'dashboard') {
      return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="orion-admin-card p-6 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Revenu Mensuel (MRR)</div>
                <div className="text-4xl font-black text-[#e0eaff]">{totalMRR.toLocaleString()} €</div>
                <div className="text-xs font-bold text-emerald-400 mt-2 flex items-center gap-1">
                    <span className="bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded text-[9px] font-black">+12%</span>
                </div>
              </div>
              <div className="orion-admin-card p-6 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Clients Actifs</div>
                <div className="text-4xl font-black text-cyan-300">{companies.filter(c => c.status === 'active').length}</div>
              </div>
              <div className="orion-admin-card p-6 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Licences Utilisées</div>
                <div className="text-4xl font-black text-[#e0eaff]">{companies.reduce((acc, c) => acc + c.userLimit, 0)}</div>
              </div>
            </div>

            {currentUser.role === UserRole.SUPER_ADMIN && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ServerStatsWidget onNavigate={onNavigate} />
                <SecurityWidget onNavigate={onNavigate} />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="orion-admin-card p-8 rounded-2xl">
                    <h3 className="text-lg font-black text-[#e0eaff] mb-6">Répartition par Plan</h3>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={planData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                                    {planData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={chartTooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="orion-admin-card p-8 rounded-2xl">
                    <h3 className="text-lg font-black text-[#e0eaff] mb-6">Croissance du Revenu</h3>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={revenueData}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,140,210,0.15)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 12 }} />
                                <Tooltip contentStyle={chartTooltipStyle} />
                                <Area type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
      );
  }

  // --- VUE CLIENTS ---
  if (activeView === 'companies') {
      return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="orion-admin-card rounded-2xl overflow-hidden min-h-[500px]">
              <div className="p-8 border-b border-[rgba(100,140,210,0.2)] flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-black text-[#e0eaff] tracking-tight">Portefeuille Clients</h2>
                    <p className="text-xs text-[#7f93bc] font-medium mt-1">{companies.length} environnements déployés</p>
                </div>
                <button type="button"
                    onClick={() => setShowAddCompany(true)}
                    className="orion-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                  NOUVEAU CLIENT
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[#7f93bc] text-[10px] font-black uppercase tracking-widest border-b border-[rgba(100,140,210,0.15)]">
                      <th className="px-8 py-5">Entreprise</th>
                      <th className="px-8 py-5">Plan</th>
                      <th className="px-8 py-5">Licences</th>
                      <th className="px-8 py-5">Devise</th>
                      <th className="px-8 py-5">Status</th>
                      <th className="px-8 py-5 text-right">Actions Support</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(100,140,210,0.1)]">
                    {companies.map((company) => (
                      <tr key={company.id} className="hover:bg-[rgba(30,55,110,0.3)] transition-colors group">
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center font-black text-sm border border-indigo-500/20">
                                  {company.name.charAt(0)}
                              </div>
                              <div>
                                <div className="font-black text-[#e0eaff]">{company.name}</div>
                                <div className="text-[10px] text-[#546a96] font-mono">ID: {company.id}</div>
                              </div>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase border bg-[rgba(15,30,60,0.6)] text-[#7f93bc] border-[rgba(100,140,210,0.2)]">
                            {plans.find(p => p.id === company.plan)?.label || company.plan}
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-xs font-bold text-[#95a7cc]">{company.userLimit} utilisateurs</div>
                        </td>
                        <td className="px-8 py-6">
                           <div className="text-xs font-bold text-[#7f93bc]">{company.currency || 'EUR'}</div>
                        </td>
                        <td className="px-8 py-6">
                            <button type="button"
                                onClick={() => toggleStatus(company)}
                                disabled={currentUser.role !== UserRole.SUPER_ADMIN}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${company.status === 'active' ? 'bg-emerald-500' : 'bg-rose-400'} ${currentUser.role !== UserRole.SUPER_ADMIN ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${company.status === 'active' ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button type="button"
                                onClick={() => setEditingCompany(company)}
                                className="p-2 text-[#546a96] hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors border border-transparent hover:border-cyan-500/20"
                                title="Modifier Abonnement"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </button>
                            <button type="button"
                                onClick={() => impersonateCompany(company)}
                                className="orion-primary-btn text-white px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
                                Accéder
                            </button>
                            {currentUser.role === UserRole.SUPER_ADMIN && (
                                <button type="button"
                                    onClick={() => handleDeleteCompany(company.id)}
                                    className="p-2 text-rose-400/50 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                    title="Supprimer Client"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {showAddCompany && <AddCompanyModal onClose={() => setShowAddCompany(false)} onSubmit={handleCreateCompany} />}
            {editingCompany && <EditCompanyModal company={editingCompany} onClose={() => setEditingCompany(null)} onSubmit={handleUpdateCompany} />}
        </div>
      );
  }

  // --- VUE TEAM ---
  if (activeView === 'team') {
      return (
          <div className="space-y-6 animate-in fade-in duration-500">
             <div className="orion-admin-card rounded-2xl overflow-hidden min-h-[500px]">
             <div className="p-8 border-b border-[rgba(100,140,210,0.2)] flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-black text-[#e0eaff] tracking-tight">Équipe Interne</h2>
                    <p className="text-xs text-[#7f93bc] font-medium mt-1">Gestion des Super Admins et de l'équipe Support.</p>
                </div>
                {currentUser.role === UserRole.SUPER_ADMIN && (
                    <button type="button"
                        onClick={() => setShowAddSystemUser(true)}
                        className="orion-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black"
                    >
                    + AJOUTER UN MEMBRE
                    </button>
                )}
             </div>
             <table className="w-full text-left">
                  <thead>
                    <tr className="text-[#7f93bc] text-[10px] font-black uppercase tracking-widest border-b border-[rgba(100,140,210,0.15)]">
                      <th className="px-8 py-5">Identité</th>
                      <th className="px-8 py-5">Rôle Système</th>
                      <th className="px-8 py-5">Permissions</th>
                      <th className="px-8 py-5 text-center">Statut</th>
                      {currentUser.role === UserRole.SUPER_ADMIN && (
                        <th className="px-8 py-5 text-right">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(100,140,210,0.1)]">
                    {systemUsers.map((admin) => (
                      <tr key={admin.id} className="hover:bg-[rgba(30,55,110,0.3)] transition-colors group">
                        <td className="px-8 py-6">
                            <div className="flex items-center gap-4">
                                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${admin.email}`} className="w-10 h-10 rounded-xl bg-[rgba(15,30,60,0.6)] border border-[rgba(100,140,210,0.2)]" />
                                <div>
                                    <div className="font-black text-[#e0eaff]">{admin.name}</div>
                                    <div className="text-xs text-[#7f93bc]">{admin.email}</div>
                                </div>
                            </div>
                        </td>
                        <td className="px-8 py-6">
                           <span className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase border ${admin.role === UserRole.SUPER_ADMIN ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/25' : 'bg-amber-500/15 text-amber-300 border-amber-500/25'}`}>
                               {admin.role === UserRole.SUPER_ADMIN ? 'Super Admin' : 'Support User'}
                           </span>
                        </td>
                        <td className="px-8 py-6">
                            <div className="text-xs text-[#7f93bc] font-medium max-w-xs">
                                {admin.role === UserRole.SUPER_ADMIN
                                    ? "Accès total, suspension clients, suppression."
                                    : "Accès CRM clients (impersonate), lecture seule config."}
                            </div>
                        </td>
                        <td className="px-8 py-6 text-center">
                           <span className="text-xs font-bold text-emerald-400 inline-flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Actif
                           </span>
                        </td>
                        {currentUser.role === UserRole.SUPER_ADMIN && (
                          <td className="px-8 py-6 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button type="button"
                                onClick={() => setEditingSystemUser({ ...admin, password: '' })}
                                className="p-2 text-[#546a96] hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors border border-transparent hover:border-cyan-500/20"
                                title="Modifier"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </button>
                              {admin.id !== currentUser.id && (
                                <button type="button"
                                  onClick={() => handleDeleteSystemUser(admin.id)}
                                  className="p-2 text-rose-400/50 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                  title="Supprimer"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
             </table>
           </div>

           {/* MODALE ÉDITION MEMBRE */}
           {editingSystemUser && (
             <div className="orion-modal-backdrop fixed inset-0 z-[150] flex items-center justify-center p-6">
               <div className="orion-modal-panel rounded-[2rem] p-8 w-full max-w-md shadow-2xl border border-[rgba(100,140,210,0.25)]">
                 <div className="flex items-center gap-4 mb-6">
                   <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${editingSystemUser.email}`} alt="Avatar" className="w-12 h-12 rounded-xl bg-[rgba(15,30,60,0.6)] border border-[rgba(100,140,210,0.2)]" />
                   <div>
                     <h3 className="text-xl font-black text-[#e0eaff]">Modifier le membre</h3>
                     <p className="text-xs text-[#546a96] font-mono">ID: {editingSystemUser.id}</p>
                   </div>
                 </div>
                 <form onSubmit={handleUpdateSystemUser} className="space-y-4">
                   <div>
                     <label htmlFor="edit-sys-name" className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Nom complet</label>
                     <input
                       id="edit-sys-name"
                       required
                       type="text"
                       value={editingSystemUser.name}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, name: e.target.value })}
                       className="orion-admin-input w-full mt-1"
                       placeholder="Nom complet"
                     />
                   </div>
                   <div>
                     <label htmlFor="edit-sys-email" className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Email</label>
                     <input
                       id="edit-sys-email"
                       required
                       type="email"
                       value={editingSystemUser.email}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, email: e.target.value })}
                       className="orion-admin-input w-full mt-1"
                       placeholder="email@exemple.com"
                     />
                   </div>
                   <div>
                     <label htmlFor="edit-sys-role" className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Rôle</label>
                     <select
                       id="edit-sys-role"
                       title="Rôle système"
                       value={editingSystemUser.role}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, role: e.target.value as UserRole })}
                       className="orion-admin-input w-full mt-1"
                     >
                       <option value="SUPER_ADMIN">Super Admin</option>
                       <option value="COMPANY_ADMIN">Company Admin</option>
                       <option value="USER">User</option>
                     </select>
                   </div>
                   <div>
                     <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Nouveau mot de passe <span className="text-[#546a96] normal-case">(laisser vide pour ne pas changer)</span></label>
                     <input
                       type="password"
                       value={editingSystemUser.password || ''}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, password: e.target.value })}
                       className="orion-admin-input w-full mt-1"
                       placeholder="••••••••"
                       autoComplete="new-password"
                       name={`new-password-${editingSystemUser.id}`}
                     />
                   </div>
                   <div className="flex gap-3 pt-4">
                     <button type="button" onClick={() => setEditingSystemUser(null)} className="flex-1 py-3 text-xs font-bold text-[#7f93bc] hover:text-[#e0eaff] transition-colors rounded-xl">Annuler</button>
                     <button type="submit" className="orion-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold">Sauvegarder</button>
                   </div>
                 </form>
               </div>
             </div>
           )}

           {showAddSystemUser && <AddUserModal companyId="system" onClose={() => setShowAddSystemUser(false)} onSubmit={handleCreateSystemUser} />}
          </div>
      );
  }

  // --- VUE MON COMPTE (2FA + sécurité personnelle) ---
  if (activeView === 'account') {
    return (
      <div className="orion-modal-panel rounded-2xl border max-w-3xl mx-auto">
        <SecurityTab user={currentUser} />
      </div>
    );
  }

  // --- VUE AUDIT LOG ---
  if (activeView === 'audit') {
    return <AuditLogView />;
  }

  // --- VUE CHARGE SERVEUR ---
  if (activeView === 'server') {
    return <ServerStatsView />;
  }

  // --- VUE SÉCURITÉ ---
  if (activeView === 'security') {
    return <SecurityView />;
  }

  // --- VUE PARAMÈTRES GLOBAUX ---
  if (activeView === 'settings') {
      return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-20">
            {/* GESTION DES PLANS */}
            <div className="orion-admin-card p-10 rounded-2xl">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h3 className="text-2xl font-black text-[#e0eaff] mb-2">Plans & Tarification</h3>
                        <p className="text-[#7f93bc] text-sm">Définissez les offres commerciales (Mensuel / Annuel).</p>
                    </div>
                    {currentUser.role === UserRole.SUPER_ADMIN && (
                        <button type="button" onClick={startNewPlan} className="orion-primary-btn text-white px-5 py-2.5 rounded-xl text-xs font-black">
                            + Nouveau Plan
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan) => (
                        <div key={plan.id} className="p-6 rounded-2xl border border-[rgba(100,140,210,0.2)] bg-[rgba(15,30,60,0.5)] hover:bg-[rgba(20,40,80,0.6)] hover:border-[rgba(100,160,230,0.35)] transition-all group relative">
                            {currentUser.role === UserRole.SUPER_ADMIN && (
                                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button type="button" onClick={() => startEditPlan(plan)} className="p-1.5 bg-[rgba(15,30,60,0.8)] rounded-lg border border-[rgba(100,140,210,0.25)] text-[#7f93bc] hover:text-cyan-300 transition-colors">✎</button>
                                    <button type="button" onClick={() => deletePlan(plan.id)} className="p-1.5 bg-[rgba(15,30,60,0.8)] rounded-lg border border-[rgba(100,140,210,0.25)] text-[#7f93bc] hover:text-rose-400 transition-colors">×</button>
                                </div>
                            )}
                            <div className="text-xs font-black text-[#7f93bc] uppercase tracking-widest mb-4">{plan.label} <span className="text-[9px] bg-[rgba(100,140,210,0.15)] text-[#546a96] px-1.5 py-0.5 rounded ml-1">{plan.id}</span></div>
                            <div className="space-y-4">
                                <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
                                    <span className="text-[10px] font-bold text-[#546a96] uppercase">Mensuel</span>
                                    <span className="font-black text-[#e0eaff]">{plan.priceMonthly} €</span>
                                </div>
                                <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
                                    <span className="text-[10px] font-bold text-[#546a96] uppercase">Annuel</span>
                                    <span className="font-black text-[#e0eaff]">{plan.priceYearly} €</span>
                                </div>
                                <div className="flex justify-between items-end">
                                    <span className="text-[10px] font-bold text-[#546a96] uppercase">Utilisateurs</span>
                                    <span className="font-black text-cyan-300">{plan.userLimit}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* GESTION DES DEVISES */}
            <div className="orion-admin-card p-10 rounded-2xl">
                <h3 className="text-2xl font-black text-[#e0eaff] mb-2">Devises & Système</h3>
                <p className="text-[#7f93bc] text-sm mb-8">Monnaies disponibles pour les environnements clients.</p>

                <div className="flex flex-wrap gap-3 mb-6">
                    {currencies.map(curr => (
                        <div key={curr.code} className="px-4 py-3 bg-indigo-500/15 text-indigo-300 font-bold rounded-xl border border-indigo-500/25 flex items-center gap-3">
                            <span className="text-lg">{curr.symbol}</span>
                            <div className="flex flex-col">
                                <span className="text-xs font-black">{curr.code}</span>
                                <span className="text-[9px] uppercase tracking-wide text-indigo-400/70">{curr.name}</span>
                            </div>
                            {currentUser.role === UserRole.SUPER_ADMIN && (
                                <button type="button" onClick={() => removeCurrency(curr.code)} className="ml-2 hover:text-rose-400 font-bold text-lg transition-colors">×</button>
                            )}
                        </div>
                    ))}
                </div>

                {currentUser.role === UserRole.SUPER_ADMIN && (
                    <div className="flex gap-4 max-w-2xl bg-[rgba(10,22,48,0.6)] p-4 rounded-2xl border border-[rgba(100,140,210,0.2)]">
                        <input
                            type="text"
                            placeholder="Code (ex: JPY)"
                            value={newCurrency.code}
                            onChange={(e) => setNewCurrency({...newCurrency, code: e.target.value.toUpperCase()})}
                            className="orion-admin-input w-24"
                            maxLength={3}
                        />
                        <input
                            type="text"
                            placeholder="Symbole (ex: ¥)"
                            value={newCurrency.symbol}
                            onChange={(e) => setNewCurrency({...newCurrency, symbol: e.target.value})}
                            className="orion-admin-input w-24"
                            maxLength={3}
                        />
                        <input
                            type="text"
                            placeholder="Nom (ex: Yen Japonais)"
                            value={newCurrency.name}
                            onChange={(e) => setNewCurrency({...newCurrency, name: e.target.value})}
                            className="orion-admin-input flex-1"
                        />
                        <button type="button"
                            onClick={addCurrency}
                            className="orion-primary-btn text-white px-5 py-2 rounded-xl font-black text-xs"
                        >
                            Ajouter
                        </button>
                    </div>
                )}
            </div>

            {/* SMTP GLOBAL — Récupération mot de passe */}
            <div className="orion-admin-card p-10 rounded-2xl">
                <h3 className="text-2xl font-black text-[#e0eaff] mb-2">SMTP Global — Récupération de mot de passe</h3>
                <p className="text-[#7f93bc] text-sm mb-2">Serveur SMTP utilisé pour envoyer les emails de réinitialisation de mot de passe.</p>
                <p className="text-[10px] text-[#546a96] mb-8">Priorité : SMTP du client → SMTP global (ci-dessous) → mail() natif PHP</p>

                <form onSubmit={async (e) => {
                    e.preventDefault();
                    setGlobalSmtpSaving(true);
                    setGlobalSmtpMsg(null);
                    try {
                        const payload: any = { smtp_host: globalSmtp.smtp_host, smtp_port: globalSmtp.smtp_port, smtp_enc: globalSmtp.smtp_enc, smtp_user: globalSmtp.smtp_user, from_name: globalSmtp.from_name, from_email: globalSmtp.from_email };
                        if (globalSmtp.smtp_pass) payload.smtp_pass = globalSmtp.smtp_pass;
                        await api.saveGlobalSmtp(payload);
                        setGlobalSmtp(prev => ({ ...prev, smtp_pass: '' }));
                        setGlobalSmtpHasPassword(true);
                        setGlobalSmtpMsg({ type: 'success', text: 'Configuration SMTP globale enregistrée.' });
                    } catch (err: any) {
                        setGlobalSmtpMsg({ type: 'error', text: err.message || 'Erreur sauvegarde SMTP' });
                    }
                    setGlobalSmtpSaving(false);
                }} className="space-y-5 max-w-2xl">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                            <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1">Serveur SMTP</label>
                            <input type="text" value={globalSmtp.smtp_host} onChange={e => setGlobalSmtp({ ...globalSmtp, smtp_host: e.target.value })}
                              placeholder="smtp.gmail.com" required className="orion-admin-input w-full mt-1" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1">Port</label>
                            <input type="number" value={globalSmtp.smtp_port} onChange={e => setGlobalSmtp({ ...globalSmtp, smtp_port: Number(e.target.value) })}
                              placeholder="587" required className="orion-admin-input w-full mt-1" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1 mb-1">Chiffrement</label>
                        <div className="flex gap-2">
                            {(['tls', 'ssl', 'none'] as const).map(enc => (
                                <button key={enc} type="button" onClick={() => setGlobalSmtp({ ...globalSmtp, smtp_enc: enc })}
                                  className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${globalSmtp.smtp_enc === enc ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' : 'bg-[rgba(100,140,210,0.08)] text-[#546a96] border-[rgba(100,140,210,0.15)]'}`}>
                                    {enc.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1">Utilisateur SMTP</label>
                            <input type="text" value={globalSmtp.smtp_user} onChange={e => setGlobalSmtp({ ...globalSmtp, smtp_user: e.target.value })}
                              placeholder="user@gmail.com" required className="orion-admin-input w-full mt-1" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1">Mot de passe SMTP</label>
                            <input type="password" value={globalSmtp.smtp_pass} onChange={e => setGlobalSmtp({ ...globalSmtp, smtp_pass: e.target.value })}
                              placeholder={globalSmtpHasPassword ? '••••••••' : 'Mot de passe'}
                              className="orion-admin-input w-full mt-1" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1">Nom expéditeur</label>
                            <input type="text" value={globalSmtp.from_name} onChange={e => setGlobalSmtp({ ...globalSmtp, from_name: e.target.value })}
                              placeholder="Orion CRM" className="orion-admin-input w-full mt-1" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-[#546a96] uppercase tracking-widest ml-1">Email expéditeur</label>
                            <input type="email" value={globalSmtp.from_email} onChange={e => setGlobalSmtp({ ...globalSmtp, from_email: e.target.value })}
                              placeholder="noreply@orion-crm.com" className="orion-admin-input w-full mt-1" />
                        </div>
                    </div>

                    {globalSmtpMsg && (
                        <div className={`p-3 rounded-xl text-sm font-bold ${globalSmtpMsg.type === 'success' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' : 'bg-rose-500/15 text-rose-400 border border-rose-500/25'}`}>
                            {globalSmtpMsg.text}
                        </div>
                    )}

                    <button type="submit" disabled={globalSmtpSaving} className="orion-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black">
                        {globalSmtpSaving ? 'Sauvegarde...' : 'Enregistrer SMTP Global'}
                    </button>
                </form>
            </div>

            {/* MODALE ÉDITION PLAN */}
            {isEditingPlan && editingPlan && (
                <div className="orion-modal-backdrop fixed inset-0 z-[150] flex items-center justify-center p-6">
                    <div className="orion-modal-panel rounded-[2rem] p-8 w-full max-w-md shadow-2xl border border-[rgba(100,140,210,0.25)]">
                        <h3 className="text-xl font-black text-[#e0eaff] mb-6">Configuration Plan</h3>
                        <form onSubmit={handleSavePlan} className="space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">ID Plan (Unique)</label>
                                <input required type="text" value={editingPlan.id} onChange={e => setEditingPlan({...editingPlan, id: e.target.value.toUpperCase()})} className="orion-admin-input w-full mt-1" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Label Affiché</label>
                                <input required type="text" value={editingPlan.label} onChange={e => setEditingPlan({...editingPlan, label: e.target.value})} className="orion-admin-input w-full mt-1" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Prix Mensuel</label>
                                    <input required type="number" value={editingPlan.priceMonthly} onChange={e => setEditingPlan({...editingPlan, priceMonthly: parseFloat(e.target.value)})} className="orion-admin-input w-full mt-1" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Prix Annuel</label>
                                    <input required type="number" value={editingPlan.priceYearly} onChange={e => setEditingPlan({...editingPlan, priceYearly: parseFloat(e.target.value)})} className="orion-admin-input w-full mt-1" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Limite Utilisateurs</label>
                                <input required type="number" value={editingPlan.userLimit} onChange={e => setEditingPlan({...editingPlan, userLimit: parseInt(e.target.value)})} className="orion-admin-input w-full mt-1" />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="button" onClick={() => setIsEditingPlan(false)} className="flex-1 py-3 text-xs font-bold text-[#7f93bc] hover:text-[#e0eaff] transition-colors rounded-xl">Annuler</button>
                                <button type="submit" className="orion-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold">Sauvegarder</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
      );
  }

  return <div className="text-[#7f93bc]">Vue en construction</div>;
};

export default SuperAdminDashboard;
